// The arrival: the six styles the map plays when the scene appears, and the
// fade that stands in for every one of them (R163, R499, R838; plan A.5 row
// 22, the parity plan of 2026-09-04 §W3).
//
// `render/spawn.ts` is the reference and this is the same arrival on the
// WebGL2 engine. Nothing here imports it — `fast/nopixi.test.ts` walks the
// import graph from `fast/main.ts`, and that module reaches the renderer
// this client replaces. The arithmetic both clients share lives in the
// engine-free leaves `render/spawn-plan.ts` and `render/motion.ts`, and the
// two baked cells live in the atlas (`gfx/atlas.ts`, `spawn-caustic` and
// `spawn-hex`), so what is written here is the drawing and nothing else.
//
// **A veil is a screen fragment pass; an ornament is a batch.** The
// unrevealed ground covers the whole viewport, so a `mode: 'screen'` world
// pass draws it in one quad: it gets `vUV`, `vWorld` and `uRes` and never
// `uViewProj` (`gfx/worldquad.ts`), which makes it screen-fixed and unshaken
// by construction — what the reference gets from a container on the stage.
// The pass is built on `start()` and disposed on the finish, so the shipping
// frame carries no extra program when no arrival plays. The wavefronts, the
// caustic ribbon and Cell Ripple's hexes are instances in the layer's own
// two batches, drawn over the veil.
//
// **Every figure the shader reads is a uniform** (`shaders/prelude.ts`): a
// veil source states no number but `0.0`, `1.0`, `0.5` and `2.0`, and
// `spawn-shaders.test.ts` holds it to that. Every edge is a `smoothstep`,
// because the client ships without MSAA (R491).
//
// **Two styles fade the map layer by layer** (R838). The reference tweens a
// container alpha per layer; there is no container here, so Stacked Deck
// writes the territory pass's `uArrival`, the site batch's alpha and the
// fleet batch's alpha, and Rise from the Deep nudges the camera zoom through
// `view.nudge`. Neither one compiles a program variant.
//
// **The arrival holds before it plays** (R1533). A hello costs the client
// one-time work for its first frames, and the theme workshop, which plays on
// a warm scene, never pays it. So the veil stands still at the first frame of
// the style until the frame loop is calm, and the wave starts after that. A
// stall after the hold advances the arrival by one step alone, and the
// territory layer keeps its border walks until the arrival ends.
//
// The arrival is presentation and nothing else (R163). The map is live under
// it, it takes no pointer, and the first pointerdown or keydown of any kind
// skips to the finished state.

import {
  ACCENT_BRIGHT, ACCENT_COLOR, LATE_LEAD_SEC, SPAWN_CALM_FRAMES, SPAWN_CALM_MS,
  SPAWN_CELL_TINT, SPAWN_DEEP_LOW, SPAWN_DEEP_MID, SPAWN_DEEP_TOP,
  SPAWN_HAZE_ALPHA, SPAWN_HAZE_LOW, SPAWN_HAZE_MID, SPAWN_HAZE_TOP,
  SPAWN_HOLD_MAX_SEC, SPAWN_MAX_CELLS, SPAWN_STEP_MAX_MS,
} from '../../gen/presentation';
import { HEX_SIZE } from '../../world';
import type { SpawnStyle } from '../../ui/settings';
import { reducedMotion, skipOnInput, stopSkipOnInput } from '../../render/motion';
import {
  easeInOut, easeOut, hexLattice, mixColor, planSpawn,
  type LatticeCell, type SpawnKind, type SpawnPlayed,
} from '../../render/spawn-plan';
import { clamp01, smooth } from '../../render/recipe-kit';
import {
  F_ADD, F_SCREEN,
  type CameraUniforms, type DrawCtx, type FrameInfo, type Layer, type ViewRect,
} from '../layer';
import type { BatchSource, CircleSink, QuadSink, SpriteEntry } from './batches';
import type { Pipeline } from '../gfx/types';
import { cssRgb, worldProgram, type WorldEnv } from '../gfx/worldquad';
import {
  VEIL_BLOOM_WGSL, VEIL_FADE_WGSL, VEIL_RISE_WGSL, VEIL_SWEEP_WGSL, veilBodyWgsl,
} from '../shaders/wgsl/spawn';
import type { ProgramSource } from '../shaders/program';
import type { UniformSpec } from '../shaders/prelude';
import { UniformValues } from '../gfx/uniforms';

// ---------------------------------------------------------------------------
// What the arrival is given
// ---------------------------------------------------------------------------

/** The scene the arrival reads each frame. The camera may move under it. */
export interface FastSpawnScene {
  /** The home hold in CSS px: `(scene.home.x − view.x0) · cam.zoom`. The
   *  object is reused by the caller, so nothing here keeps it. */
  home(): { x: number; y: number };
  /** The camera scale, so Cell Ripple's lattice matches the map's hexes. */
  scale(): number;
  /** Fades the territory pass through its `uArrival` uniform (R838).
   *  Stacked Deck settles the ownership on top of the terrain. */
  setTerritoryAlpha(a: number): void;
  /** Fades the site layer, which settles after the ownership. */
  setSitesAlpha(a: number): void;
  /** Fades the fleet layer, which settles last of all. */
  setFleetsAlpha(a: number): void;
  /** Holds the territory layer's border walks while the wave plays, and
   *  lets them run again at the finish (R1533). A walk after a flip costs
   *  a frame, and the arrival must not drop one. */
  deferTerritory(on: boolean): void;
}

/**
 * As much of a world pass as a veil needs. `gfx/worldquad.ts`'s `WorldPass`
 * satisfies it, and stating it here keeps this module free of `gl/`.
 */
export interface VeilPass {
  setExtra(fn: ((p: Pipeline) => void) | null): void;
  draw(cam: CameraUniforms, env: WorldEnv): number;
  dispose(): void;
}

/** As much of the engine as the arrival needs: one pass, and the world. */
export interface SpawnEngine {
  newWorldPass(program: ProgramSource): VeilPass;
  readonly env: WorldEnv;
}

// ---------------------------------------------------------------------------
// The veils
// ---------------------------------------------------------------------------

/** The unrevealed ground: the background gradient, four stops deeper. */
const DEEP_TOP = cssRgb(SPAWN_DEEP_TOP);
const DEEP_MID = cssRgb(SPAWN_DEEP_MID);
const DEEP_LOW = cssRgb(SPAWN_DEEP_LOW);

/**
 * The deep sea every veil is painted in, and the fade they all share.
 *
 * The reference bakes a 64 × 512 gradient canvas and stretches it over the
 * viewport (`render/spawn.ts:80-89`); the same three stops down `vUV.y` cost
 * one mix here and no texture at all. Sonar Bloom and Caustic Sweep paint a
 * flat middle tone in the reference, because a growing sprite cannot carry a
 * vertical gradient — this engine has no such limit, so every veil takes the
 * gradient and the styles read as one arrival.
 */
const DEEP_GRADIENT = `
uniform vec3  uTopColor;
uniform vec3  uMidColor;
uniform vec3  uLowColor;
uniform float uVeilAlpha;

vec3 deep(float t) {
  return t < 0.5 ? mix(uTopColor, uMidColor, t * 2.0)
                 : mix(uMidColor, uLowColor, (t - 0.5) * 2.0);
}
`;

/** The three gradient stops and the fade, as every veil declares them. */
const DEEP_UNIFORMS: readonly UniformSpec[] = [
  { name: 'uTopColor', type: 'vec3', value: DEEP_TOP },
  { name: 'uMidColor', type: 'vec3', value: DEEP_MID },
  { name: 'uLowColor', type: 'vec3', value: DEEP_LOW },
  { name: 'uVeilAlpha', type: 'float', value: 1, dynamic: true },
];

/** The drawing surface in CSS px, which the two masked veils measure in. */
const SCREEN_UNIFORM: UniformSpec = {
  name: 'uScreen', type: 'vec2', value: [1, 1], dynamic: true,
};

/** One veil: the fragment body, its twin, and the uniforms both declare. */
export interface VeilSpec {
  readonly name: string;
  /** The body. `WORLD_PRELUDE` is prepended by the pass, so it declares no
   *  `#version`, no precision statement and none of the A.6 contract. */
  readonly fragment: string;
  /** The WGSL twin of that body: the `@fragment fn fs` entry alone. The
   *  header, the screen vertex twin and the deep gradient are joined to it
   *  by `worldProgram`, the one assembly rule of the engine. */
  readonly wgsl: string;
  readonly uniforms: readonly UniformSpec[];
}

/** One veil's program, as both backends read it. */
export function veilProgram(spec: VeilSpec): ProgramSource {
  return worldProgram({
    name: spec.name, mode: 'screen', fragment: spec.fragment, uniforms: spec.uniforms,
    wgslBody: veilBodyWgsl(spec.wgsl),
  });
}

/** The plain fade: the deep sea over the map, lifting (`spawn.ts:225-244`). */
const VEIL_FADE: VeilSpec = {
  name: 'spawn-fade',
  fragment: `${DEEP_GRADIENT}
void main() {
  float a = uVeilAlpha;
  o = vec4(deep(vUV.y) * a, a);
}`,
  wgsl: VEIL_FADE_WGSL,
  uniforms: DEEP_UNIFORMS,
};

/**
 * Sonar Bloom's hole (`spawn.ts:94-103, 264-294`).
 *
 * The reference opens the hole with a radial-gradient sprite and covers the
 * four rectangles of screen outside that sprite's square. One `smoothstep`
 * on the distance from the home hold says the same thing with no texture and
 * no filler, and it keeps the reference's ramp: clear to `uHoleInner` of the
 * radius, and the full veil at the radius. A radius of 0 opens no hole, so
 * the hold shows the plain veil (R1533).
 */
const VEIL_BLOOM: VeilSpec = {
  name: 'spawn-bloom',
  fragment: `${DEEP_GRADIENT}
uniform vec2  uScreen;
uniform vec2  uHome;
uniform float uRadius;
uniform float uHoleInner;

void main() {
  vec2 p = vUV * uScreen;
  float hole = uRadius > 0.0
    ? smoothstep(uHoleInner * uRadius, uRadius, length(p - uHome)) : 1.0;
  float a = uVeilAlpha * hole;
  o = vec4(deep(vUV.y) * a, a);
}`,
  wgsl: VEIL_BLOOM_WGSL,
  uniforms: [
    ...DEEP_UNIFORMS,
    SCREEN_UNIFORM,
    { name: 'uHome', type: 'vec2', value: [0, 0], dynamic: true },
    { name: 'uRadius', type: 'float', value: 0, dynamic: true },
    // The reference's radial stop: clear to 78% of the radius, veiled at it.
    { name: 'uHoleInner', type: 'float', value: 0.78 },
  ],
};

/**
 * Caustic Sweep's front (`spawn.ts:108-118, 325-348`).
 *
 * The reference rotates a 1024 × 8 ramp texture whose stops sit at 0.500 and
 * 0.525 of its width, over a sprite four screen diagonals long — a band of a
 * tenth of a diagonal. Here the same band is a `smoothstep` on the distance
 * along the axis from the front, so the ramp needs no sprite and no texture.
 */
const VEIL_SWEEP: VeilSpec = {
  name: 'spawn-sweep',
  fragment: `${DEEP_GRADIENT}
uniform vec2  uScreen;
uniform vec2  uAxis;
uniform float uFront;
uniform float uBand;

void main() {
  vec2 p = vUV * uScreen;
  float s = dot(p, uAxis) - uFront;
  float a = uVeilAlpha * smoothstep(-uBand, uBand, s);
  o = vec4(deep(vUV.y) * a, a);
}`,
  wgsl: VEIL_SWEEP_WGSL,
  uniforms: [
    ...DEEP_UNIFORMS,
    SCREEN_UNIFORM,
    { name: 'uAxis', type: 'vec2', value: [1, 0], dynamic: true },
    { name: 'uFront', type: 'float', value: 0, dynamic: true },
    { name: 'uBand', type: 'float', value: 1, dynamic: true },
  ],
};

/**
 * Rise from the Deep's pressure floor (mockup round seven, card 02; R838).
 *
 * The card paints the dark ground under the map and a three-stop haze over
 * it, and the haze is the last thing to let go. Both are ONE screen pass
 * here: the ground at `uVeilAlpha`, and the haze over it at `uHaze`, which
 * runs from 1 to 0 across its own window. The haze carries one colour and
 * one alpha per stop, so it clears from the top down as the card does.
 */
const VEIL_RISE: VeilSpec = {
  name: 'spawn-rise',
  fragment: `${DEEP_GRADIENT}
uniform vec3  uHazeTop;
uniform vec3  uHazeMid;
uniform vec3  uHazeLow;
uniform vec3  uHazeA;
uniform float uHaze;

// The haze colour and its alpha at the screen fraction t, top to bottom.
vec4 haze(float t) {
  vec3 c = t < 0.5 ? mix(uHazeTop, uHazeMid, t * 2.0)
                   : mix(uHazeMid, uHazeLow, (t - 0.5) * 2.0);
  float a = t < 0.5 ? mix(uHazeA.x, uHazeA.y, t * 2.0)
                    : mix(uHazeA.y, uHazeA.z, (t - 0.5) * 2.0);
  return vec4(c, a * uHaze);
}

void main() {
  float a = uVeilAlpha;
  vec4 g = vec4(deep(vUV.y) * a, a);
  vec4 z = haze(vUV.y);
  // The haze over the ground, both premultiplied.
  o = vec4(z.rgb * z.a + g.rgb * (1.0 - z.a), z.a + g.a * (1.0 - z.a));
}`,
  wgsl: VEIL_RISE_WGSL,
  uniforms: [
    ...DEEP_UNIFORMS,
    { name: 'uHazeTop', type: 'vec3', value: cssRgb(SPAWN_HAZE_TOP) },
    { name: 'uHazeMid', type: 'vec3', value: cssRgb(SPAWN_HAZE_MID) },
    { name: 'uHazeLow', type: 'vec3', value: cssRgb(SPAWN_HAZE_LOW) },
    { name: 'uHazeA', type: 'vec3', value: [...SPAWN_HAZE_ALPHA] },
    { name: 'uHaze', type: 'float', value: 1, dynamic: true },
  ],
};

/** Every veil source, for `spawn-shaders.test.ts`. */
export const VEILS: readonly VeilSpec[] = [VEIL_FADE, VEIL_BLOOM, VEIL_SWEEP, VEIL_RISE];

// ---------------------------------------------------------------------------
// The figures of the styles, copied from the reference and from the cards
// ---------------------------------------------------------------------------

/** Sonar Bloom's front reaches this much past the furthest corner. */
const BLOOM_OVERSHOOT = 1.06;
/** Its wavefront and two echoes: radius share, width in CSS px, alpha. */
const BLOOM_RINGS: readonly (readonly [number, number, number])[] = [
  [1, 2.4, 0.85], [0.66, 1.4, 0.3], [0.36, 1.1, 0.16],
];
/** A ring under this radius in CSS px is not drawn (`spawn.ts:290`). */
const RING_FLOOR_PX = 2;

/** Caustic Sweep's axis, normalised, and the shape of its band. */
const SWEEP_AX = 0.816;
const SWEEP_AY = 0.578;
/** The band is a tenth of a screen diagonal (`spawn.ts:333`). */
const SWEEP_BAND_FRAC = 0.1;
/** The ribbon quad: 1.6 bands wide and three diagonals long. */
const SWEEP_LIGHT_W = 1.6;
const SWEEP_LIGHT_H = 3;
const SWEEP_LIGHT_ALPHA = 0.9;

/** Cell Ripple: the texture's hex circumradius, and the cell's own curve. */
const HEX_TEX_R = 64;
const CELL_FRONT_LEAD = 0.72;
const CELL_FRONT_WIDTH = 0.26;
const CELL_SCALE_TOP = 1.02;
const CELL_SCALE_DROP = 0.47;
const CELL_LIT_FLOOR = 0.05;
const CELL_LIT_MIX = 0.85;

/** Seaquake Settle: the shudder, and the shockwave the centre throws out. */
const QUAKE_DECAY = 8.8;
const QUAKE_AMP_PX = 6.5;
const QUAKE_FREQ_X = 11;
const QUAKE_FREQ_Y = 15;
const QUAKE_AMP_Y_FRAC = 0.7;
const QUAKE_RING_REACH = 1.25;
/** The shockwave is gone by this point of the play (`spawn.ts:546`). */
const QUAKE_RING_END = 0.92;
const QUAKE_RINGS: readonly (readonly [number, number, number])[] = [
  [1, 2.8, 0.8], [0.62, 1.5, 0.34],
];
const QUAKE_RING_BRIGHT = 0xeafffb;
const QUAKE_RING_DIM = 0x9fe8f5;
const QUAKE_RING_FLOOR_PX = 3;

/** Seaquake Settle's veil lifts fast: the map lands, it never fades. */
const QUAKE_VEIL_TO = 0.1;

/**
 * Rise from the Deep (card 02, R838). The view opens this much too large and
 * settles, about an anchor this far down the screen.
 */
const RISE_ZOOM_FROM = 1.055;
const RISE_ANCHOR_Y = 0.55;
/** The map holds this much of its light at the floor, and it climbs on a
 *  power curve rather than a straight line: the last of the climb is slow. */
const RISE_ALPHA_FLOOR = 0.04;
const RISE_ALPHA_POW = 1.25;
/** The haze clears over this window of the play, and it clears last. */
const RISE_HAZE_FROM = 0.06;
const RISE_HAZE_TO = 0.92;

/**
 * Stacked Deck (card 04, R838). One window per layer, in the z-order the
 * scene already has: the terrain, the territory, the sites and the fleets,
 * each a beat behind the one before.
 */
const DECK_BASE: readonly [number, number] = [0, 0.34];
const DECK_TERRITORY: readonly [number, number] = [0.2, 0.56];
const DECK_SITES: readonly [number, number] = [0.44, 0.76];
const DECK_FLEETS: readonly [number, number] = [0.62, 0.96];

/** How far one layer of the deck has settled at `u`, 0 to 1. */
function deckStep(w: readonly [number, number], u: number): number {
  return easeOut(smooth(w[0], w[1], u));
}

// ---------------------------------------------------------------------------
// The runners
// ---------------------------------------------------------------------------

/** What a style is handed each frame. One object, reused for the whole run. */
interface Host {
  scene: FastSpawnScene;
  /** The drawing surface in CSS px. */
  w: number;
  h: number;
  /** The visible world rect and the camera scale, so a screen figure at
   *  `(cx, cy)` is the world point `(view.x0 + cx / zoom, …)`. */
  view: ViewRect;
  zoom: number;
  quads: QuadSink;
  circles: CircleSink;
  sprite(key: string): SpriteEntry;
  /** The veil pass's own uniform set, or null for a style with no veil. */
  veil: UniformValues | null;
  shake(x: number, y: number): void;
  /** The arrival's zoom nudge: a scale about a screen anchor (R838). */
  nudge(k: number, ax: number, ay: number): void;
}

/**
 * One arrival. `apply` gets `u` (0 → 1 across the play) and `t` (seconds
 * since the start, for anything that oscillates rather than progresses).
 * `dispose` puts back everything the style touched.
 */
interface Runner {
  apply(u: number, t: number, host: Host): void;
  /** Absent where the style touched nothing a finish must put back. */
  dispose?(host: Host): void;
}

/** The plain fade. Reduced motion and every resync play this (R184). */
class FadeRunner implements Runner {
  apply(u: number, _t: number, host: Host): void {
    host.veil?.set('uVeilAlpha', 1 - smooth(0, 1, u));
  }
}

/**
 * 01 Sonar Bloom (the default). One wavefront leaves the home hold and
 * lights the map as it passes; two echoes trail it.
 */
class SonarBloomRunner implements Runner {
  private readonly screen = [0, 0];
  private readonly home = [0, 0];

  constructor(host: Host) {
    host.veil?.set('uScreen', this.screen);
    host.veil?.set('uHome', this.home);
  }

  apply(u: number, _t: number, host: Host): void {
    const { w, h } = host;
    const home = host.scene.home();
    // The furthest corner, so the front leaves no ground unlit.
    const maxd = Math.max(
      1,
      Math.hypot(home.x, home.y),
      Math.hypot(w - home.x, home.y),
      Math.hypot(home.x, h - home.y),
      Math.hypot(w - home.x, h - home.y),
    );
    const r = easeOut(u) * maxd * BLOOM_OVERSHOOT;

    this.screen[0] = w; this.screen[1] = h;
    this.home[0] = home.x; this.home[1] = home.y;
    host.veil?.set('uVeilAlpha', 1);
    // Under 1 CSS px the hole stays shut: a hole that small shows the map
    // as one bright pixel for the whole hold.
    host.veil?.set('uRadius', r >= 1 ? r : 0);

    // The wavefront and its echoes, in world px with screen-fixed widths.
    const wx = host.view.x0 + home.x / host.zoom;
    const wy = host.view.y0 + home.y / host.zoom;
    const fade = (1 - u) * (1 - u * 0.2);
    for (const [k, width, a] of BLOOM_RINGS) {
      const rr = r * k;
      if (rr < RING_FLOOR_PX) continue;
      host.circles.ring(
        wx, wy, rr / host.zoom, width,
        k === 1 ? ACCENT_BRIGHT : ACCENT_COLOR, a * fade, F_SCREEN,
      );
    }
  }
}

/**
 * 06 Caustic Sweep. A band of surface light crosses the map on the diagonal
 * and leaves the world behind it.
 */
class CausticSweepRunner implements Runner {
  private readonly screen = [0, 0];
  private readonly axis = [SWEEP_AX, SWEEP_AY];

  constructor(host: Host) {
    host.veil?.set('uScreen', this.screen);
    host.veil?.set('uAxis', this.axis);
  }

  apply(u: number, _t: number, host: Host): void {
    const { w, h } = host;
    // The band crosses at close to a constant speed and eases out at the far
    // edge; a slow start would leave the map dark for a third of the play.
    const e = u * 0.7 + easeOut(u) * 0.3;
    const diag = Math.hypot(w, h);
    const half = (w * SWEEP_AX + h * SWEEP_AY) / 2;
    const band = diag * SWEEP_BAND_FRAC;
    const s = -half - band + e * (2 * half + 2 * band);
    const cx = w / 2 + SWEEP_AX * s;
    const cy = h / 2 + SWEEP_AY * s;
    const ang = Math.atan2(SWEEP_AY, SWEEP_AX);

    this.screen[0] = w; this.screen[1] = h;
    host.veil?.set('uVeilAlpha', 1);
    // The axis is a unit vector, so the front's own scalar is the centre's.
    host.veil?.set('uFront', cx * SWEEP_AX + cy * SWEEP_AY);
    host.veil?.set('uBand', band / 2);

    // The light that rides the front: one additive quad of the baked
    // ribbons, its right edge on the front (`gfx/atlas.ts` `spawn-caustic`).
    const entry = host.sprite('spawn-caustic');
    const qw = band * SWEEP_LIGHT_W;
    const qh = diag * SWEEP_LIGHT_H;
    host.quads.quad(
      host.view.x0 + cx / host.zoom, host.view.y0 + cy / host.zoom,
      -qw / 2, 0, qw, qh, ang, F_SCREEN | F_ADD,
      0xffffff, SWEEP_LIGHT_ALPHA * (1 - Math.pow(u, 3)),
      entry.u0, entry.v0, entry.u1, entry.v1,
    );
  }
}

/**
 * 10 Cell Ripple. The map materialises cell by cell, a ripple of hexes
 * running out of the centre. **It shows the hex grid, which the map never
 * otherwise draws** — one deliberate look at the machine before the machine
 * hides again (R163).
 *
 * There is no veil pass: the cells ARE the veil, one instance each in the
 * layer's quad batch, and the lattice is capped at `SPAWN_MAX_CELLS`.
 */
class CellRippleRunner implements Runner {
  private cells: LatticeCell[] = [];
  private baseScale = 1;
  private built = -1;

  constructor(host: Host) { this.build(host); }

  private build(host: Host): void {
    const home = host.scene.home();
    const size = HEX_SIZE * host.scene.scale();
    const lat = hexLattice(host.w, host.h, size, home.x, home.y);
    this.cells = lat.cells;
    // The cell is a hex of circumradius 64 in its own box, so this scale
    // puts the drawn hex on the lattice's pitch. The rim straddles the edge,
    // which closes the seam between two cells.
    this.baseScale = lat.size / HEX_TEX_R;
    this.built = host.w * 100000 + host.h;
  }

  apply(u: number, _t: number, host: Host): void {
    if (this.built !== host.w * 100000 + host.h) this.build(host);
    const entry = host.sprite('spawn-hex');
    const e = easeInOut(u);
    const x0 = host.view.x0, y0 = host.view.y0, z = host.zoom;
    for (const c of this.cells) {
      // v runs 0 → 1 as the front passes this cell; 1 means "gone".
      const v = clamp01((e - c.d * CELL_FRONT_LEAD) / CELL_FRONT_WIDTH);
      if (v >= 1) continue;
      // The rim of a cell the front is crossing lights up, so the ripple has
      // a visible edge. A settled cell keeps the grid tone.
      const lit = 4 * v * (1 - v);
      const tint = lit > CELL_LIT_FLOOR
        ? mixColor(SPAWN_CELL_TINT, ACCENT_COLOR, lit * CELL_LIT_MIX)
        : SPAWN_CELL_TINT;
      host.quads.sprite(
        x0 + c.x / z, y0 + c.y / z, 0, entry, tint, 1 - v,
        this.baseScale * (CELL_SCALE_TOP - CELL_SCALE_DROP * v), F_SCREEN,
      );
    }
  }
}

/**
 * 08 Seaquake Settle. The map does not fade in, it lands: a shockwave leaves
 * the centre and everything settles to stillness.
 */
class QuakeSettleRunner implements Runner {
  apply(u: number, t: number, host: Host): void {
    const { w, h } = host;
    const arrive = smooth(0, QUAKE_VEIL_TO, u);
    host.veil?.set('uVeilAlpha', 1 - arrive);

    const amp = QUAKE_AMP_PX * Math.exp(-QUAKE_DECAY * u);
    host.shake(
      Math.sin(t * 2 * Math.PI * QUAKE_FREQ_X) * amp,
      Math.cos(t * 2 * Math.PI * QUAKE_FREQ_Y) * amp * QUAKE_AMP_Y_FRAC,
    );

    if (u > QUAKE_RING_END) return;
    const reach = easeOut(u) * Math.hypot(w / 2, h / 2) * QUAKE_RING_REACH;
    const wx = host.view.x0 + (w / 2) / host.zoom;
    const wy = host.view.y0 + (h / 2) / host.zoom;
    for (const [k, width, a] of QUAKE_RINGS) {
      const r = reach * k;
      if (r < QUAKE_RING_FLOOR_PX) continue;
      host.circles.ring(
        wx, wy, r / host.zoom, width,
        k === 1 ? QUAKE_RING_BRIGHT : QUAKE_RING_DIM,
        a * (1 - u) * arrive, F_SCREEN,
      );
    }
  }

  dispose(host: Host): void { host.shake(0, 0); }
}

/**
 * 02 Rise from the Deep. The map starts at the pressure floor: near black,
 * faintly blue and slightly too large. Light climbs into it as the view
 * ascends, and the haze is the last thing to let go.
 *
 * The lift is the camera's own `view.nudge` (R838): the layer publishes the
 * three figures and the frame copies them, the way it copies the shudder.
 */
class DeepRiseRunner implements Runner {
  apply(u: number, _t: number, host: Host): void {
    // The view settles from RISE_ZOOM_FROM to 1 on an ease in and out, so
    // neither end of the climb has a visible step.
    host.nudge(RISE_ZOOM_FROM - (RISE_ZOOM_FROM - 1) * easeInOut(u), 0.5, RISE_ANCHOR_Y);
    const lit = RISE_ALPHA_FLOOR + (1 - RISE_ALPHA_FLOOR) * Math.pow(u, RISE_ALPHA_POW);
    host.veil?.set('uVeilAlpha', 1 - lit);
    host.veil?.set('uHaze', 1 - smooth(RISE_HAZE_FROM, RISE_HAZE_TO, u));
  }

  dispose(host: Host): void { host.nudge(1, 0.5, 0.5); }
}

/**
 * 04 Stacked Deck. The scene assembles in its own z-order: the terrain, then
 * the territory, then the sites, then the fleets, each a beat behind the one
 * before. It states the map's structure and nothing else.
 *
 * The card also lifts each layer seven px into place. The engine has no
 * per-layer screen offset, and the z-order stagger is the card's identity, so
 * the lift is dropped (R838; R163 dropped Seaquake Settle's slice ripple for
 * the same reason).
 */
class StackedDeckRunner implements Runner {
  constructor(host: Host) {
    // Frame one shows the sea alone: every layer over the terrain starts at 0.
    host.scene.setTerritoryAlpha(0);
    host.scene.setSitesAlpha(0);
    host.scene.setFleetsAlpha(0);
  }

  apply(u: number, _t: number, host: Host): void {
    host.veil?.set('uVeilAlpha', 1 - deckStep(DECK_BASE, u));
    host.scene.setTerritoryAlpha(deckStep(DECK_TERRITORY, u));
    host.scene.setSitesAlpha(deckStep(DECK_SITES, u));
    host.scene.setFleetsAlpha(deckStep(DECK_FLEETS, u));
  }
}

/** One style: its runner and the veil it draws (`render/spawn.ts:565-572`). */
interface StyleEntry {
  runner(host: Host): Runner;
  /** The veil's source, or null: Cell Ripple draws none, its cells are it. */
  veil: VeilSpec | null;
}

const STYLES: Record<SpawnPlayed, StyleEntry> = {
  'sonar-bloom': { runner: (h) => new SonarBloomRunner(h), veil: VEIL_BLOOM },
  'caustic-sweep': { runner: (h) => new CausticSweepRunner(h), veil: VEIL_SWEEP },
  'cell-ripple': { runner: (h) => new CellRippleRunner(h), veil: null },
  'seaquake-settle': { runner: () => new QuakeSettleRunner(), veil: VEIL_FADE },
  'deep-rise': { runner: () => new DeepRiseRunner(), veil: VEIL_RISE },
  'stacked-deck': { runner: (h) => new StackedDeckRunner(h), veil: VEIL_FADE },
  fade: { runner: () => new FadeRunner(), veil: VEIL_FADE },
};

// ---------------------------------------------------------------------------
// The layer
// ---------------------------------------------------------------------------

/** The rect a `start()` before the first frame reads. It is replaced on the
 *  first `update`, and never written to. */
const NO_VIEW: ViewRect = { x0: 0, y0: 0, x1: 0, y1: 0 };

export class SpawnLayer implements Layer {
  readonly name = 'spawn';
  private shown = true;

  /** The extra camera shudder Seaquake Settle asks for, in CSS px. The
   *  caller adds it to the other sources and gates the sum on the
   *  screenshake switch, in ONE `camera.setShake` call (R164). */
  shakeX = 0;
  shakeY = 0;

  /** The zoom nudge Rise from the Deep asks for (R838): a scale about the
   *  screen anchor `(nudgeAx, nudgeAy)`. The caller copies the three into
   *  `view.nudge` each frame, the way it copies the shudder. */
  nudgeK = 1;
  nudgeAx = 0.5;
  nudgeAy = 0.5;

  /** What last played. Presentation only; the tests and the harness read it. */
  played: SpawnPlayed | null = null;

  /**
   * Called once when `LATE_LEAD_SEC` of the run remains. The chrome dock-in
   * starts here rather than on `onEnd`, so the panels land as the arrival
   * closes (R182). A skip before this point never fires it — `onEnd` covers
   * that path, and the two are safe to hang the same call on.
   */
  onLate: (() => void) | null = null;
  /** Called once when a run ends, whether it ran out or a player skipped it.
   *  A run that a new `start()` supersedes does not call it. */
  onEnd: (() => void) | null = null;
  /**
   * Called once when the hold ends and the wave starts (R1533). The
   * arrival's cue hangs off it, so the sound starts with the motion. A skip
   * during the hold calls it before `onEnd`, so the cue still sounds as the
   * map appears.
   */
  onPlay: (() => void) | null = null;

  private readonly quads: QuadSink;
  private readonly circles: CircleSink;
  private readonly host: Host;
  private runner: Runner | null = null;
  private pass: VeilPass | null = null;
  private elapsed = 0;
  private playSec = 0;
  private lateFired = false;
  /** The hold (R1533): true until the frame loop is calm. */
  private holding = false;
  /** The calm frames in a row, and the frame time the hold started at. */
  private calm = 0;
  private holdFrom = -1;
  private skipper: (() => void) | null = null;

  constructor(private readonly engine: SpawnEngine, batches: BatchSource) {
    // Cell Ripple is the widest of them all, and its lattice is capped, so
    // one allocation at boot covers every style and no frame ever grows a
    // batch mid-arrival.
    this.quads = batches.newQuads(SPAWN_MAX_CELLS);
    this.circles = batches.newCircles(8);
    this.host = {
      scene: EMPTY_SCENE,
      w: 0, h: 0, view: NO_VIEW, zoom: 1,
      quads: this.quads, circles: this.circles,
      sprite: (key) => batches.sprite(key),
      veil: null,
      shake: (x, y) => { this.shakeX = x; this.shakeY = y; },
      nudge: (k, ax, ay) => { this.nudgeK = k; this.nudgeAx = ax; this.nudgeAy = ay; },
    };
  }

  get visible(): boolean { return this.shown; }
  setVisible(v: boolean): void { this.shown = v; }
  get running(): boolean { return this.runner !== null; }

  /**
   * Plays the arrival for this hello. Any run still on screen finishes
   * first, so two helloes in a row cannot leave a veil behind.
   *
   * `veil` false plays the style's ornaments and its shudder over a map
   * that is already there (R1535). The reel page passes it for the
   * shockwave of Seaquake Settle in the middle of a fight. Every other
   * caller leaves it true.
   */
  start(style: SpawnStyle, kind: SpawnKind, scene: FastSpawnScene, veil = true): void {
    this.finish();
    const plan = planSpawn(style, kind, reducedMotion());
    this.playSec = plan.playSec;
    this.played = plan.played;
    this.elapsed = 0;
    this.lateFired = false;
    this.holding = true;
    this.calm = 0;
    this.holdFrom = -1;

    const host = this.host;
    host.scene = scene;
    host.veil = null;

    const entry = STYLES[plan.played];
    // The veil's pass and its uniform set are built here and dropped on the
    // finish, so the shipping frame carries no extra program (R499). They
    // stand before the runner, whose constructor may write the set.
    const spec = veil ? entry.veil : null;
    if (spec) {
      const values = new UniformValues(spec.uniforms);
      const pass = this.engine.newWorldPass(veilProgram(spec));
      pass.setExtra((p) => values.apply(p));
      this.pass = pass;
      host.veil = values;
    }
    this.runner = entry.runner(host);

    // Skippable (R163): a returning player sees this every reload, so the
    // first input of any kind jumps to the finished state. The event is not
    // swallowed — the map is live under the arrival, and a click that skips
    // it may also select the fleet it landed on.
    const skip = () => this.skip();
    this.skipper = skip;
    skipOnInput(skip);
  }

  /** Jumps to the finished state. Any pointerdown or keydown calls this. */
  skip(): void { if (this.runner) this.finish(true); }

  update(frame: FrameInfo): void {
    const runner = this.runner;
    if (!runner) return;
    if (this.holding) {
      // The veil stands at the style's first frame while the hello's
      // one-time work runs: the first draw of every layer, the atlas cells,
      // the first state frame. Calm frames in a row end the hold, and the
      // cap ends it on a device that is never calm.
      if (this.holdFrom < 0) this.holdFrom = frame.now;
      this.calm = frame.dtMs <= SPAWN_CALM_MS ? this.calm + 1 : 0;
      const held = (frame.now - this.holdFrom) / 1000;
      if (this.calm >= SPAWN_CALM_FRAMES || held >= SPAWN_HOLD_MAX_SEC) {
        this.endHold();
        // The handler may have skipped the run.
        if (!this.runner) return;
      }
    } else {
      // A stall advances the arrival by one step alone, so the wave pauses
      // with the frame and does not jump.
      this.elapsed += Math.min(frame.dtMs, SPAWN_STEP_MAX_MS) / 1000;
    }
    if (this.elapsed >= this.playSec) { this.finish(true); return; }
    if (!this.lateFired && this.playSec - this.elapsed <= LATE_LEAD_SEC) {
      this.lateFired = true;
      this.onLate?.();
      // The handler may have skipped the run, and a disposed runner must not
      // be applied.
      if (!this.runner) return;
    }
    const host = this.host;
    host.w = frame.cam.w;
    host.h = frame.cam.h;
    host.view = frame.view;
    host.zoom = frame.cam.zoom;
    this.quads.begin();
    this.circles.begin();
    runner.apply(this.elapsed / this.playSec, this.elapsed, host);
  }

  draw(ctx: DrawCtx): void {
    if (!this.runner) return;
    // The veil first and the ornaments over it: the wavefront of Sonar Bloom
    // and the ribbon of Caustic Sweep ride the front they light.
    this.pass?.draw(ctx.cam, this.engine.env);
    this.quads.flush(ctx.cam);
    this.circles.flush(ctx.cam);
  }

  /** For a teardown of the whole client. */
  dispose(): void { this.finish(); }

  /** The wave starts: the border walks wait, and the cue sounds. */
  private endHold(): void {
    this.holding = false;
    this.host.scene.deferTerritory(true);
    this.onPlay?.();
  }

  /**
   * Tears the arrival down: the veil pass disposed, the three layer alphas
   * back to 1, and the shudder and the zoom nudge zeroed. Nothing costs
   * anything one frame after this.
   *
   * @param fire true when the run reached its own end — it ran out, or a
   * player skipped it — and `onEnd` is owed a call. A teardown that another
   * `start()` or a `dispose()` forces passes false.
   */
  private finish(fire = false): void {
    const runner = this.runner;
    const ran = runner !== null;
    this.stopSkipper();
    // A skip during the hold still owes the cue.
    if (fire && ran && this.holding) this.onPlay?.();
    this.holding = false;
    if (runner) {
      runner.dispose?.(this.host);
      this.host.scene.deferTerritory(false);
      // The three layer alphas, on every exit path and not only Stacked
      // Deck's: a batch left dim by an arrival that ended some other way
      // would stay dim for the session.
      this.host.scene.setTerritoryAlpha(1);
      this.host.scene.setSitesAlpha(1);
      this.host.scene.setFleetsAlpha(1);
    }
    this.runner = null;
    this.pass?.dispose();
    this.pass = null;
    this.host.veil = null;
    this.host.scene = EMPTY_SCENE;
    this.shakeX = 0;
    this.shakeY = 0;
    this.nudgeK = 1;
    this.nudgeAx = 0.5;
    this.nudgeAy = 0.5;
    // The instances of the last frame go with it, so a batch the layer no
    // longer draws holds nothing.
    this.quads.begin();
    this.circles.begin();
    // Last, and with the state already clean: the handler is free to start
    // whatever comes next.
    if (fire && ran) this.onEnd?.();
  }

  private stopSkipper(): void {
    if (!this.skipper) return;
    stopSkipOnInput(this.skipper);
    this.skipper = null;
  }
}

/** The scene the host holds between runs: it is never read. */
const EMPTY_SCENE: FastSpawnScene = {
  home: () => ({ x: 0, y: 0 }),
  scale: () => 1,
  setTerritoryAlpha: () => { /* nothing is playing */ },
  setSitesAlpha: () => { /* nothing is playing */ },
  setFleetsAlpha: () => { /* nothing is playing */ },
  deferTerritory: () => { /* nothing is playing */ },
};
